import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { MESSAGES } from 'src/app/core/constants/messages';
import { ConnectionGraphicProperties } from 'src/app/core/models/3D/graphic-properties/connection-graphic-properties.model';
import { Connection } from 'src/app/core/models/connection.model';
import { Floating } from 'src/app/core/models/floating.model';
import { NodeType } from 'src/app/core/models/load/node/node-type.enum';
import { Point3d } from 'src/app/core/models/point3d.model';
import { ReferenceType } from 'src/app/core/models/reference-type.enum';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { PMath } from 'src/app/shared/utils/recurrent-math/project-math';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { EnvironmentPropsService } from '../../../environment-props/environment-props.service';
import { FeedbackService } from '../../../feedback/feedback.service';
import { NodesService } from '../../load/nodes/nodes.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { LineService } from '../line/line.service';
import { MapsService } from '../maps/maps.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  constructor(
    private simulationService: SimulationManagerService,
    private dataSharingService: DataSharingService,
    private mapsService: MapsService,
    private lineService: LineService,
    private nodesService: NodesService,
    private environmentPropsService: EnvironmentPropsService,
    private feedbackService: FeedbackService,
  ) {}

  get simulation(): Simulation {
    return this.simulationService.getSimulation();
  }

  assignDefaultConnection(simulation: Simulation): void {
    if (this.environmentPropsService.isAnflex) {
      return;
    }
    const newConnection = this.getNew();
    newConnection.title = 'Default Hidden Connection';
    newConnection.localCoordinates.x = 0;
    newConnection.localCoordinates.y = 0;
    newConnection.localCoordinates.z = simulation.global[0]?.seabedDepth;
    if (simulation?.connection?.length > 0) {
      simulation.connection.push(newConnection);
    } else {
      simulation.connection = [newConnection];
    }

    if (simulation?.line?.length > 0) {
      simulation.line.forEach((lineElem) => {
        if (lineElem.connectionId === null || lineElem.connectionId <= 0) {
          lineElem.connectionId = newConnection.internalId;
        }
      });
    }

    this.mapsService.updateObject(newConnection, CONSTANTS.LINE_BINDING_TYPES.CONNECTION);
  }

  getById(id: number): Connection {
    const answer = this.simulation?.connection?.find((conn) => conn.internalId === id);
    return answer || null;
  }

  getNew(): Connection {
    const newConnectionId = this.simulationService.getNewId();
    const newConnection = this.getNewEmpty();
    newConnection.internalId = newConnectionId;
    newConnection.title = `C${newConnectionId}`;
    return newConnection;
  }

  getNewEmpty(): Connection {
    const newConnection: Connection = new Connection();
    newConnection.referenceId = -1;
    newConnection.reference = ReferenceType.GLOBAL;
    newConnection.localCoordinates = new Point3d();
    newConnection.localCoordinates.x = 0;
    newConnection.localCoordinates.y = 0;
    newConnection.localCoordinates.z = this.simulation.global[0].seabedDepth;
    newConnection.needsUpdate = true;
    newConnection.graphicProperties = new ConnectionGraphicProperties();
    return newConnection;
  }

  save(payload: any, currentReferenceSelection: string): void {
    this.dataSharingService.loading$.next(true);

    const connections: Connection[] = [];
    this.simulation.connection?.forEach((element) => {
      element.graphicProperties?.delete();
    });

    payload?.connections?.forEach((element) => {
      const newConnection = this.getNewEmpty();

      const noInternalId = element.internalId === null || element.internalId <= 0;
      newConnection.internalId = noInternalId
        ? this.simulationService.getNewId()
        : element.internalId;

      if (element?.movementCenter !== null && element?.movementCenter >= 0) {
        newConnection.referenceId = element?.movementCenter;
        newConnection.reference = null;
      } else {
        newConnection.referenceId = -1;
        newConnection.reference = ReferenceType.GLOBAL;
      }

      let { x: newX, y: newY, z: newZ } = element;

      if (currentReferenceSelection !== ReferenceType.LOCAL && element?.movementCenter >= 0) {
        const floating = this.mapsService.getObjectById(element.movementCenter) as Floating;
        if (floating) {
          const rotation = PMath.azimuthDegToRotationRad(floating.azimuth);
          const xFloating = floating?.origin?.x;
          const yFloating = floating?.origin?.y;
          const zFloating = this.simulation.global[0].seabedDepth - floating?.draft;

          const translationX = newX - xFloating;
          const translationY = newY - yFloating;
          newX = Math.cos(rotation) * translationX + Math.sin(rotation) * translationY;
          newY = -Math.sin(rotation) * translationX + Math.cos(rotation) * translationY;
          newZ -= zFloating;
        }
      }

      newConnection.localCoordinates = Object.assign(newConnection.localCoordinates, {
        x: newX,
        y: newY,
        z: newZ,
      });
      newConnection.title = element?.title || `C${newConnection.internalId}`;
      newConnection.needsUpdate = true;
      newConnection.graphicProperties = new ConnectionGraphicProperties();
      connections.push(newConnection);
      this.updateRelatedNode(newConnection);

      this.mapsService.updateObject(newConnection, CONSTANTS.LINE_BINDING_TYPES.CONNECTION);
    });

    this.handleDeletedConnections(this.simulation.connection, connections);
    this.simulation.connection = connections;
    this.adjustLinesConnections(connections);
    this.simulationService.changeSimulation(false);
    this.dataSharingService.updateCatenary$.next();

    const usedByLinesWithIds = this.simulation.line.map(({ internalId }) => internalId);
    this.lineService.markToUpdate(usedByLinesWithIds);
    this.dataSharingService.loading$.next(false);
  }

  updateRelatedNode(connection: Connection): void {
    const nodeFound = this.simulation.node.find(
      ({ referenceId }) => referenceId === connection.internalId,
    );
    if (!nodeFound) {
      this.nodesService.create(connection, NodeType.CONNECTION);
      return;
    }
    this.nodesService.refreshReferences(connection, NodeType.CONNECTION);
  }

  handleDeletedConnections(
    previousConnections: Connection[],
    currentConnections: Connection[],
  ): void {
    previousConnections.forEach((prevConnection) => {
      const stillExists = currentConnections.find(
        (currConnection) => currConnection.internalId === prevConnection.internalId,
      );
      if (!stillExists) {
        prevConnection?.graphicProperties?.delete();
        this.nodesService.deleteByReferenceId(prevConnection.internalId);
      }
    });
  }

  adjustLinesConnections(connections: Connection[]): void {
    if (this.simulation?.line?.length > 0) {
      const linesReset = [];
      this.simulation.line.forEach((line) => {
        if (line?.connectionId !== -1) {
          const matchingConnections = connections.filter(
            (connection) => connection.internalId === line?.connectionId,
          );
          if (matchingConnections?.length <= 0) {
            line.connectionId = -1;
            line.needsUpdate = true;
            linesReset.push(line.title);
          }
        }
      });

      if (!linesReset.length) {
        return;
      }
      this.feedbackService.warning(
        `${MESSAGES.WARNING.CONNECTION_RESET_FOR_LINES} [Line(s): ${linesReset.join(', ')}]`,
      );
    }
  }

  getAsGlobal(connection: Connection): Connection {
    if (connection.reference === ReferenceType.GLOBAL) {
      return connection;
    }

    const floating = this.mapsService.getObjectById(connection.referenceId) as Floating;

    let newX = connection?.localCoordinates?.x;
    let newY = connection?.localCoordinates?.y;
    let newZ = connection?.localCoordinates?.z;

    if (floating) {
      const rotation = PMath.azimuthDegToRotationRad(floating.azimuth);
      const floatingX = floating?.origin?.x;
      const floatingY = floating?.origin?.y;
      const floatingZ = this.simulation.global[0].seabedDepth - floating?.draft;

      const localX = newX;
      const localY = newY;
      newX = floatingX + localX * Math.cos(rotation) - localY * Math.sin(rotation);
      newY = floatingY + localX * Math.sin(rotation) + localY * Math.cos(rotation);
      newZ += floatingZ;
    }

    const connectionCopy = Clone(connection) as Connection;
    connectionCopy.referenceId = connection?.referenceId;
    connectionCopy.localCoordinates.x = newX;
    connectionCopy.localCoordinates.y = newY;
    connectionCopy.localCoordinates.z = newZ;
    return connectionCopy;
  }

  isObjectInUse(id: number): string[] | null {
    const foundInLines = this.simulation.line
      .filter((line) => line?.connectionId === id)
      .map((line) => line.title);
    return foundInLines?.length ? foundInLines : null;
  }

  isObjectByIdInUse(id: number): number[] | null {
    const foundInLines = this.simulation.line
      .filter((line) => line?.connectionId === id)
      .map((line) => line.internalId);
    return foundInLines?.length ? foundInLines : null;
  }

  markAllToUpdate(): void {
    this.simulation.connection.forEach((connection) => {
      connection.needsUpdate = true;
    });
  }
}
